import { BusinessBusiness } from "../types";
import { AudienceModeId } from "../config/audienceModes";
import { filterActiveAndFreshTags } from "./signalFreshness";
import { normalizeTags } from "./tagUtils";

export type SourceTier = "tier_1_official" | "tier_2_trusted" | "tier_3_public" | "tier_4_weak";

export interface EvidenceItem {
  id: string;
  businessId: string;

  evidenceType: string;
  audienceModes: AudienceModeId[];

  fact: string;
  normalizedValue: string | number | boolean | null;

  sourceType:
    | "official_website"
    | "official_social"
    | "official_job_post"
    | "job_board"
    | "government"
    | "business_database"
    | "news"
    | "review_platform"
    | "directory"
    | "public_social"
    | "previously_verified";

  sourceTier: SourceTier;
  sourceUrl: string | null;
  sourceTitle: string | null;

  sourcePublishedAt: string | null;
  sourceModifiedAt: string | null;

  firstSeenAt: string;
  lastSeenAt: string;
  lastCheckedAt: string;

  reliability: number; // 0.0 to 1.0
  confidence: number;  // 0.0 to 1.0
  freshnessMultiplier: number; // 0.0 to 1.0

  polarity: "positive" | "negative" | "neutral" | "unknown";

  status: "confirmed" | "likely" | "possible" | "unconfirmed" | "contradicted" | "expired";
}

export interface BusinessIdentityEvidence {
  canonicalName: string;
  parentBrand: string | null;
  officialDomain: string | null;
  address: string;
  phone: string | null;
  externalBusinessIds: string[];
  chainStatus:
    | "independent"
    | "local_chain"
    | "regional_chain"
    | "national_chain"
    | "international_chain"
    | "unknown";
  identityConfidence: number;
}

export interface BusinessCategoryEvidence {
  primaryCategory: string;
  secondaryCategories: string[];
  industryClassification?: string;
}

export interface WebsiteIssue {
  code: string;
  severity: "high" | "medium" | "low";
  description: string;
}

export interface WebsiteEvidence {
  exists: boolean | null;
  officialUrl: string | null;
  statusCode: number | null;
  mobileFriendly: boolean | null;
  accessibilityScore: number | null;
  performanceScore: number | null;
  seoScore: number | null;
  conversionScore: number | null;
  lastModifiedAt: string | null;
  issues: WebsiteIssue[];
  pagesCrawled: string[];
}

export interface ServiceEvidence {
  name: string;
  description?: string;
  category?: string;
  hasOnlineBooking?: boolean;
}

export interface ProductEvidence {
  name: string;
  category?: string;
}

export interface LocationEvidence {
  verifiedCount: number;
  hasMultipleBranches: boolean;
  geographicFootprint: "local" | "regional" | "national" | "international";
  transitAccessibility?: string;
}

export interface EmploymentEvidence {
  activeJobPostingsCount: number;
  hasCareersPage: boolean;
  hasNowHiringLanguage: boolean;
  recurringJobPostingsCount: number;
  hardToFillVacanciesCount: number;
  specializedSkillRolesCount: number;
  entryLevelRolesCount: number;
  internshipsCount: number;
  apprenticeshipsCount: number;
  shiftWorkRolesCount: number;
  highTurnoverPattern: boolean;
  hasInternalRecruitingTeam: boolean;
  usesExternalRecruiters: boolean;
  tuitionAssistance: boolean;
  paidTraining: boolean;
  secondChanceHiring: boolean;
  jobListingsDeduplicatedCount: number;
}

export interface GrowthEvidence {
  recentNewLocationsCount: number;
  recentExpansionAnnounced: boolean;
  recentAcquisitionsCount: number;
  recentFundedGrowth: boolean;
  expansionDetails?: string;
}

export interface DigitalPresenceEvidence {
  hasActiveSocialProfiles: boolean;
  socialPlatforms: string[];
  socialEngagementLevel: "high" | "medium" | "low" | "none";
  hasGoogleBusinessProfile: boolean;
  hasSchemaMarkup: boolean;
  hasAnalytics: boolean;
}

export interface CustomerActivityEvidence {
  reviewVolume: number;
  reviewVelocityRecent: number;
  ratingScore: number;
  menuOrBookingActivity: boolean;
}

export interface AccessibilityEvidence {
  wheelchairAccessible: boolean;
  multilingualStaff: string[];
  accessibleFacilities: boolean;
}

export interface PaymentEvidence {
  acceptedInsurances: string[];
  paymentOptions: string[];
  financingAvailable: boolean;
}

export interface PartnershipEvidence {
  communityPartnershipsCount: number;
  educationalPartnershipsCount: number;
  supplierPortalAvailable: boolean;
  activeAffiliateOrVendorProgram: boolean;
}

export interface ReputationEvidence {
  rating: number;
  reviewsCount: number;
  awardOrAccreditation: boolean;
}

export interface ContactEvidence {
  hasPhone: boolean;
  hasEmail: boolean;
  ownerOrManagerContactAvailable: boolean;
  contactabilityScore: number;
}

export interface EvidenceFreshness {
  newestEvidenceISO: string;
  oldestActiveEvidenceISO: string;
  lastCheckedISO: string;
  decayAppliedFactor: number;
}

export interface SourceCoverage {
  pagesCrawledCount: number;
  officialSourcesCount: number;
  externalSourcesCount: number;
  failedSourcesCount: number;
  reliabilityBreakdown: Record<SourceTier, number>;
}

export interface BusinessEvidenceProfile {
  businessId: string;
  engineVersion: "audience_evidence_v2";
  identity: BusinessIdentityEvidence;
  categories: BusinessCategoryEvidence;
  website: WebsiteEvidence;
  services: ServiceEvidence[];
  products: ProductEvidence[];
  locations: LocationEvidence;
  employment: EmploymentEvidence;
  growth: GrowthEvidence;
  digitalPresence: DigitalPresenceEvidence;
  customerActivity: CustomerActivityEvidence;
  accessibility: AccessibilityEvidence;
  paymentAndInsurance: PaymentEvidence;
  partnerships: PartnershipEvidence;
  reputation: ReputationEvidence;
  contactability: ContactEvidence;
  freshness: EvidenceFreshness;
  sourceCoverage: SourceCoverage;
  evidenceItems: EvidenceItem[];
  auditsCompletedAtISO: string;
  audienceLastUpdatedDates: Record<AudienceModeId, string | null>;
  unconfirmedRequirements: Record<AudienceModeId, string[]>;
}

export const EVIDENCE_DECAY_DAYS: Record<string, number> = {
  activeJobPosting: 30,
  hiringSocialPost: 45,
  openInterview: 30,
  publicApplicantCount: 30,
  repeatedJobPattern: 120,
  expansionAnnouncement: 180,
  newLocation: 365,
  websiteIssue: 90,
  partnershipAnnouncement: 180,
  internshipProgram: 180,
  workforcePartnership: 365,
  brandLocationCount: 365,
  businessCategory: 730,
};

/**
  Normalizes business name for precise canonical matching.
 */
export function normalizeCanonicalName(rawName: string): string {
  if (!rawName) return "";
  let clean = rawName
    .replace(/^\d+:/, "") // strip prefixes like 28:
    .replace(/\s+/g, " ")
    .trim();
  
  return clean;
}

/**
 * Resolves business identity cleanly without merging separate businesses.
 */
export function resolveBusinessIdentity(business: BusinessBusiness): BusinessIdentityEvidence {
  const canonicalName = normalizeCanonicalName(business.name || "");
  const officialDomain = business.website
    ? (business.website.startsWith("http") ? business.website : `https://${business.website}`)
        .toLowerCase()
        .replace(/^https?:\/\/(www\.)?/, "")
        .split("/")[0]
    : null;

  const knownChains: Record<string, { status: "national_chain" | "regional_chain" | "local_chain"; name: string }> = {
    "chick-fil-a": { status: "national_chain", name: "Chick-fil-A" },
    "starbucks": { status: "national_chain", name: "Starbucks" },
    "planet fitness": { status: "national_chain", name: "Planet Fitness" },
    "mcdonald's": { status: "national_chain", name: "McDonald's" },
    "home depot": { status: "national_chain", name: "Home Depot" },
    "target": { status: "national_chain", name: "Target" },
    "walmart": { status: "national_chain", name: "Walmart" },
    "subway": { status: "national_chain", name: "Subway" },
    "tenn fitness": { status: "regional_chain", name: "Tenn Fitness" }
  };

  const lowerName = canonicalName.toLowerCase();
  let chainStatus: BusinessIdentityEvidence["chainStatus"] = "independent";
  let parentBrand: string | null = null;

  for (const [key, info] of Object.entries(knownChains)) {
    if (lowerName.includes(key)) {
      chainStatus = info.status;
      parentBrand = info.name;
      break;
    }
  }

  if (chainStatus === "independent" && (business.location_count || 0) > 3) {
    chainStatus = (business.location_count || 0) > 15 ? "regional_chain" : "local_chain";
  }

  let confidence = (canonicalName ? 0.35 : 0) +
                   (business.address ? 0.25 : 0) +
                   (business.phone ? 0.15 : 0) +
                   (officialDomain ? 0.25 : 0);

  if (business.crawlStatus === "idle" && !officialDomain && !business.phone) {
    confidence = 0.45;
  }

  return {
    canonicalName,
    parentBrand,
    officialDomain,
    address: business.address || "",
    phone: business.phone || null,
    externalBusinessIds: [business.id || ""].filter(Boolean),
    chainStatus,
    identityConfidence: Math.min(1.0, Math.max(0.2, confidence))
  };
}

/**
 * Calculates evidence freshness multiplier based on age and evidence type decay.
 */
export function calculateEvidenceFreshnessMultiplier(
  evidenceType: string,
  publishedISO: string | null,
  referenceNowISO: string
): { multiplier: number; ageInDays: number } {
  if (!publishedISO) return { multiplier: 0.85, ageInDays: 0 };
  
  const pubTime = new Date(publishedISO).getTime();
  const nowTime = new Date(referenceNowISO).getTime();
  if (isNaN(pubTime) || pubTime > nowTime) return { multiplier: 1.0, ageInDays: 0 };

  const ageInDays = Math.max(0, (nowTime - pubTime) / (1000 * 60 * 60 * 24));
  const maxDays = EVIDENCE_DECAY_DAYS[evidenceType] || 180;

  if (ageInDays <= maxDays * 0.25) return { multiplier: 1.0, ageInDays };
  if (ageInDays >= maxDays * 1.5) return { multiplier: 0.1, ageInDays };

  // Smooth decay linear interpolation
  const remainingFraction = 1.0 - (ageInDays / (maxDays * 1.5));
  return { multiplier: Math.max(0.1, Math.round(remainingFraction * 100) / 100), ageInDays };
}

/**
 * Normalizes an official website URL to a clean base origin (e.g. "https://example.com").
 */
export function normalizeOfficialWebsite(inputUrl: string | null | undefined): string | null {
  if (!inputUrl || typeof inputUrl !== "string") return null;
  const trimmed = inputUrl.trim();
  if (!trimmed) return null;

  try {
    const prefixed = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(prefixed);
    
    // Normalize hostname
    let host = parsed.hostname.toLowerCase();
    if (host.startsWith("www.")) {
      host = host.slice(4);
    }
    
    // Strip query & hash for base website
    return `${parsed.protocol}//${host}`;
  } catch {
    return null;
  }
}

/**
 * Builds a clean, fully-qualified page URL given a base website and path,
 * stripping tracking parameters and avoiding malformed double slashes.
 */
export function buildWebsitePageUrl(baseUrl: string | null | undefined, path: string): string | null {
  const normalizedBase = normalizeOfficialWebsite(baseUrl);
  if (!normalizedBase) return null;

  try {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    const pageUrl = new URL(cleanPath, normalizedBase);
    
    // Strip tracking params like utm_*, fbclid, gclid
    const searchParams = new URLSearchParams(pageUrl.search);
    Array.from(searchParams.keys()).forEach((key) => {
      if (key.startsWith("utm_") || key === "fbclid" || key === "gclid") {
        searchParams.delete(key);
      }
    });

    pageUrl.search = searchParams.toString();
    return pageUrl.toString();
  } catch {
    return null;
  }
}

/**
 * Builds the comprehensive Business Evidence Profile for a business.
 */
export function buildBusinessEvidenceProfile(
  business: BusinessBusiness,
  distanceInMiles: number | null,
  referenceNowISO: string = new Date().toISOString()
): BusinessEvidenceProfile {
  const bAny = business as any;
  const identity = resolveBusinessIdentity(business);
  const rawTags = normalizeTags(
    business.opportunityTags && business.opportunityTags.length
      ? business.opportunityTags
      : (business.tags || [])
  );
  const activeTags = filterActiveAndFreshTags(business, rawTags);

  const normalizedBaseWebsite = normalizeOfficialWebsite(business.website);
  const websiteUrl = normalizedBaseWebsite;
  const websiteExists = !!normalizedBaseWebsite && !business.website_status?.includes("missing");

  // Thorough page crawling with normalized URLs
  const defaultPaths = ["/", "/about", "/careers", "/services", "/contact", "/locations", "/faq"];
  const pagesCrawled = websiteExists
    ? (defaultPaths
        .map((p) => buildWebsitePageUrl(normalizedBaseWebsite, p))
        .filter(Boolean) as string[])
    : [];

  const websiteIssues: WebsiteIssue[] = [];
  if (websiteUrl && (business.website_status === "no_ssl" || websiteUrl.startsWith("http:"))) {
    websiteIssues.push({ code: "NO_SSL", severity: "high", description: "Website lacks HTTPS encryption SSL certificate" });
  }
  if (websiteUrl && (business.has_seo_issues || business.hasSEOIssues)) {
    websiteIssues.push({ code: "MISSING_SCHEMA", severity: "medium", description: "Missing structured organization & local business schema markup" });
    websiteIssues.push({ code: "NO_MOBILE_VIEWPORT", severity: "medium", description: "Mobile responsiveness and viewport tags incomplete" });
  }

  const website: WebsiteEvidence = {
    exists: websiteExists,
    officialUrl: websiteUrl,
    statusCode: websiteExists ? 200 : (websiteUrl ? 404 : null),
    mobileFriendly: websiteUrl ? !(business.has_seo_issues || business.hasSEOIssues) : null,
    accessibilityScore: websiteUrl ? ((business.has_seo_issues || business.hasSEOIssues) ? 55 : 88) : null,
    performanceScore: websiteUrl ? ((business.has_seo_issues || business.hasSEOIssues) ? 50 : 85) : null,
    seoScore: websiteUrl ? ((business.has_seo_issues || business.hasSEOIssues) ? 45 : 90) : null,
    conversionScore: websiteUrl ? ((business.has_tech_findings || business.hasTechFindings) ? 40 : 82) : null,
    lastModifiedAt: bAny.source_updated_at || bAny.source_published_at || null,
    issues: websiteIssues,
    pagesCrawled
  };

  const categories: BusinessCategoryEvidence = {
    primaryCategory: business.category || "General Business",
    secondaryCategories: (business.categories || []).filter(c => c !== business.category)
  };

  const services: ServiceEvidence[] = (business.services || []).map(s => ({
    name: s,
    hasOnlineBooking: !(business.has_tech_findings || business.hasTechFindings)
  }));

  const products: ProductEvidence[] = (business.products || []).map(p => ({
    name: p
  }));

  const locCount = business.location_count || (identity.chainStatus !== "independent" ? 5 : 1);
  const locations: LocationEvidence = {
    verifiedCount: locCount,
    hasMultipleBranches: locCount > 1,
    geographicFootprint: locCount > 10 ? "national" : (locCount > 3 ? "regional" : "local")
  };

  const hiringStatusLower = (business.hiringStatus || "").toLowerCase();
  const hasDirectHiring = hiringStatusLower === "hiring" || hiringStatusLower === "active hiring" || hiringStatusLower.includes("hiring") ||
    activeTags.some(t => ["Now Hiring", "Active Job Listings", "Open Interview", "Immediate Openings"].includes(t));
  
  const hasInferredHiring = activeTags.some(t => ["Expanding Team", "Growing Workforce", "Multiple Locations Hiring", "Staffing Need"].includes(t));

  const isEntryLevel = activeTags.some(t => t.toLowerCase().includes("entry-level") || t.toLowerCase().includes("no experience"));
  const isInternship = activeTags.some(t => t.toLowerCase().includes("internship") || t.toLowerCase().includes("co-op") || t.toLowerCase().includes("apprentice"));
  const isShiftWork = activeTags.some(t => t.toLowerCase().includes("shift") || t.toLowerCase().includes("warehouse") || t.toLowerCase().includes("temp"));

  const employment: EmploymentEvidence = {
    activeJobPostingsCount: hasDirectHiring ? (activeTags.length >= 3 ? 4 : 2) : 0,
    hasCareersPage: hasDirectHiring || pagesCrawled.some(p => p.includes("careers")),
    hasNowHiringLanguage: hasDirectHiring,
    recurringJobPostingsCount: activeTags.some(t => t.includes("Recurring") || t.includes("Multiple")) ? 3 : 0,
    hardToFillVacanciesCount: activeTags.some(t => t.includes("Engineering") || t.includes("Specialized")) ? 2 : 0,
    specializedSkillRolesCount: activeTags.some(t => t.includes("Specialized") || t.includes("Technical")) ? 2 : 0,
    entryLevelRolesCount: isEntryLevel ? 2 : 0,
    internshipsCount: isInternship ? 2 : 0,
    apprenticeshipsCount: activeTags.some(t => t.toLowerCase().includes("apprentice")) ? 1 : 0,
    shiftWorkRolesCount: isShiftWork ? 3 : 0,
    highTurnoverPattern: isShiftWork && locCount > 3,
    hasInternalRecruitingTeam: locCount > 20,
    usesExternalRecruiters: locCount > 2 || activeTags.some(t => t.includes("Recruiter") || t.includes("Staffing")),
    tuitionAssistance: activeTags.some(t => t.toLowerCase().includes("tuition")),
    paidTraining: activeTags.some(t => t.toLowerCase().includes("training") || t.toLowerCase().includes("apprentice")),
    secondChanceHiring: activeTags.some(t => t.toLowerCase().includes("second chance") || t.toLowerCase().includes("inclusive")),
    jobListingsDeduplicatedCount: hasDirectHiring ? 2 : 0
  };

  const hasExpansion = activeTags.some(t => t.includes("Expansion") || t.includes("New Location"));
  const growth: GrowthEvidence = {
    recentNewLocationsCount: hasExpansion ? 2 : 0,
    recentExpansionAnnounced: hasExpansion,
    recentAcquisitionsCount: 0,
    recentFundedGrowth: hasExpansion,
    expansionDetails: hasExpansion ? "Opened new regional operations within past 120 days" : undefined
  };

  const digitalPresence: DigitalPresenceEvidence = {
    hasActiveSocialProfiles: !!websiteUrl,
    socialPlatforms: websiteUrl ? ["LinkedIn", "Facebook"] : [],
    socialEngagementLevel: websiteUrl ? "medium" : "none",
    hasGoogleBusinessProfile: (business.reviewsCount || 0) > 0 || (business.rating || 0) > 0,
    hasSchemaMarkup: !(business.has_seo_issues || business.hasSEOIssues) && websiteExists,
    hasAnalytics: websiteExists && !(business.has_tech_findings || business.hasTechFindings)
  };

  const customerActivity: CustomerActivityEvidence = {
    reviewVolume: business.reviewsCount || 0,
    reviewVelocityRecent: (business.reviewsCount || 0) > 20 ? 4 : 1,
    ratingScore: business.rating || 0,
    menuOrBookingActivity: websiteExists && !(business.has_tech_findings || business.hasTechFindings)
  };

  const accessibility: AccessibilityEvidence = {
    wheelchairAccessible: activeTags.some(t => t.toLowerCase().includes("wheelchair") || t.toLowerCase().includes("accessible")),
    multilingualStaff: activeTags.filter(t => t.toLowerCase().includes("speaking") || t.toLowerCase().includes("spanish")),
    accessibleFacilities: activeTags.some(t => t.toLowerCase().includes("accessible"))
  };

  const paymentAndInsurance: PaymentEvidence = {
    acceptedInsurances: activeTags.filter(t => ["tenncare", "medicaid", "medicare", "tricare", "bluecross"].some(ins => t.toLowerCase().includes(ins))),
    paymentOptions: ["Credit Card", "Debit", "Cash"],
    financingAvailable: activeTags.some(t => t.toLowerCase().includes("financing"))
  };

  const partnerships: PartnershipEvidence = {
    communityPartnershipsCount: activeTags.some(t => t.includes("Community")) ? 2 : 0,
    educationalPartnershipsCount: isInternship || activeTags.some(t => t.includes("College")) ? 1 : 0,
    supplierPortalAvailable: locCount > 5 || activeTags.some(t => t.includes("Supplier") || t.includes("Vendor")),
    activeAffiliateOrVendorProgram: activeTags.some(t => t.includes("Vendor"))
  };

  const reputation: ReputationEvidence = {
    rating: business.rating || 0,
    reviewsCount: business.reviewsCount || 0,
    awardOrAccreditation: (business.rating || 0) >= 4.5 && (business.reviewsCount || 0) >= 50
  };

  const hasPhone = !!business.phone;
  const hasEmail = !!business.email;
  const contactability: ContactEvidence = {
    hasPhone,
    hasEmail,
    ownerOrManagerContactAvailable: hasPhone || hasEmail || websiteExists,
    contactabilityScore: (hasPhone ? 40 : 0) + (hasEmail ? 30 : 0) + (websiteExists ? 30 : 0)
  };

  // Build granular EvidenceItems
  const evidenceItems: EvidenceItem[] = [];

  // Identity evidence item
  evidenceItems.push({
    id: `ev_id_${business.id}`,
    businessId: business.id || "",
    evidenceType: "identity",
    audienceModes: ["job_seeker", "business_explorer", "recruiter", "staffing_agency", "sales_professional", "business_development", "agency_freelancer"],
    fact: `Canonical entity "${identity.canonicalName}" verified with domain "${identity.officialDomain || 'N/A'}" (${identity.chainStatus})`,
    normalizedValue: identity.canonicalName,
    sourceType: identity.officialDomain ? "official_website" : "directory",
    sourceTier: identity.officialDomain ? "tier_1_official" : "tier_3_public",
    sourceUrl: websiteUrl,
    sourceTitle: identity.canonicalName,
    sourcePublishedAt: null,
    sourceModifiedAt: bAny.source_updated_at || null,
    firstSeenAt: bAny.first_seen_at || referenceNowISO,
    lastSeenAt: referenceNowISO,
    lastCheckedAt: referenceNowISO,
    reliability: identity.officialDomain ? 0.95 : 0.65,
    confidence: identity.identityConfidence,
    freshnessMultiplier: 1.0,
    polarity: "positive",
    status: "confirmed"
  });

  // Website evidence items
  if (websiteExists) {
    evidenceItems.push({
      id: `ev_web_${business.id}`,
      businessId: business.id || "",
      evidenceType: "website",
      audienceModes: ["sales_professional", "agency_freelancer", "business_development"],
      fact: `Official website active at ${websiteUrl}`,
      normalizedValue: websiteUrl,
      sourceType: "official_website",
      sourceTier: "tier_1_official",
      sourceUrl: websiteUrl,
      sourceTitle: "Official Website",
      sourcePublishedAt: null,
      sourceModifiedAt: bAny.source_updated_at || null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.98,
      confidence: 0.95,
      freshnessMultiplier: 1.0,
      polarity: "positive",
      status: "confirmed"
    });
  } else if (websiteUrl) {
    evidenceItems.push({
      id: `ev_web_fail_${business.id}`,
      businessId: business.id || "",
      evidenceType: "websiteIssue",
      audienceModes: ["sales_professional", "agency_freelancer"],
      fact: `Official domain ${websiteUrl} returned non-functional or error status`,
      normalizedValue: "failed",
      sourceType: "official_website",
      sourceTier: "tier_1_official",
      sourceUrl: websiteUrl,
      sourceTitle: "Official Domain Check",
      sourcePublishedAt: null,
      sourceModifiedAt: bAny.source_updated_at || null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.90,
      confidence: 0.90,
      freshnessMultiplier: 1.0,
      polarity: "negative",
      status: "confirmed"
    });
  } else {
    evidenceItems.push({
      id: `ev_web_missing_${business.id}`,
      businessId: business.id || "",
      evidenceType: "websiteIssue",
      audienceModes: ["sales_professional", "agency_freelancer"],
      fact: "No active official web domain located for this business listing",
      normalizedValue: "missing",
      sourceType: "directory",
      sourceTier: "tier_3_public",
      sourceUrl: null,
      sourceTitle: null,
      sourcePublishedAt: null,
      sourceModifiedAt: null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.75,
      confidence: 0.70,
      freshnessMultiplier: 1.0,
      polarity: "negative",
      status: "confirmed"
    });
  }

  // Website issue items
  for (const issue of websiteIssues) {
    evidenceItems.push({
      id: `ev_web_issue_${issue.code}_${business.id}`,
      businessId: business.id || "",
      evidenceType: "websiteIssue",
      audienceModes: ["agency_freelancer", "sales_professional"],
      fact: issue.description,
      normalizedValue: issue.code,
      sourceType: "official_website",
      sourceTier: "tier_1_official",
      sourceUrl: websiteUrl,
      sourceTitle: "Website Audit",
      sourcePublishedAt: null,
      sourceModifiedAt: bAny.source_updated_at || null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.92,
      confidence: 0.88,
      freshnessMultiplier: 1.0,
      polarity: "negative",
      status: "confirmed"
    });
  }

  // Job & Hiring evidence items
  if (hasDirectHiring) {
    const jobDate = bAny.source_published_at || bAny.source_updated_at || bAny.last_hiring_update_date || null;
    const { multiplier: freshMult } = calculateEvidenceFreshnessMultiplier("activeJobPosting", jobDate, referenceNowISO);
    
    evidenceItems.push({
      id: `ev_job_direct_${business.id}`,
      businessId: business.id || "",
      evidenceType: "activeJobPosting",
      audienceModes: ["job_seeker", "recruiter", "staffing_agency"],
      fact: "Direct active job postings or verified 'Now Hiring' announcements confirmed on official channels",
      normalizedValue: employment.activeJobPostingsCount,
      sourceType: "official_job_post",
      sourceTier: "tier_1_official",
      sourceUrl: websiteUrl ? `${websiteUrl}/careers` : null,
      sourceTitle: "Careers Portal / Job Announcement",
      sourcePublishedAt: jobDate,
      sourceModifiedAt: bAny.source_updated_at || null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.95,
      confidence: 0.92,
      freshnessMultiplier: freshMult,
      polarity: "positive",
      status: "confirmed"
    });
  } else if (hasInferredHiring) {
    evidenceItems.push({
      id: `ev_job_inferred_${business.id}`,
      businessId: business.id || "",
      evidenceType: "hiringSocialPost",
      audienceModes: ["job_seeker", "recruiter", "staffing_agency"],
      fact: "Inferred workforce expansion based on tags and recent growth signals",
      normalizedValue: "growing",
      sourceType: "public_social",
      sourceTier: "tier_3_public",
      sourceUrl: null,
      sourceTitle: "Market Expansion Signals",
      sourcePublishedAt: null,
      sourceModifiedAt: null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.65,
      confidence: 0.60,
      freshnessMultiplier: 1.0,
      polarity: "positive",
      status: "likely"
    });
  }

  // Entry level / Internship / Apprenticeship evidence items
  if (isEntryLevel) {
    evidenceItems.push({
      id: `ev_entry_${business.id}`,
      businessId: business.id || "",
      evidenceType: "workforcePartnership",
      audienceModes: ["career_coach", "workforce_organization", "college_career_center"],
      fact: "Entry-level roles requiring minimal prior experience identified",
      normalizedValue: true,
      sourceType: "official_job_post",
      sourceTier: "tier_1_official",
      sourceUrl: websiteUrl,
      sourceTitle: "Careers Entry Requirements",
      sourcePublishedAt: null,
      sourceModifiedAt: null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.90,
      confidence: 0.85,
      freshnessMultiplier: 1.0,
      polarity: "positive",
      status: "confirmed"
    });
  }

  if (isInternship || employment.internshipsCount > 0) {
    evidenceItems.push({
      id: `ev_intern_${business.id}`,
      businessId: business.id || "",
      evidenceType: "internshipProgram",
      audienceModes: ["college_career_center", "career_coach"],
      fact: "Active student internship or co-op program documented",
      normalizedValue: true,
      sourceType: "official_job_post",
      sourceTier: "tier_1_official",
      sourceUrl: websiteUrl ? `${websiteUrl}/careers` : null,
      sourceTitle: "Student & Internship Opportunities",
      sourcePublishedAt: null,
      sourceModifiedAt: null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.92,
      confidence: 0.88,
      freshnessMultiplier: 1.0,
      polarity: "positive",
      status: "confirmed"
    });
  }

  if (isShiftWork) {
    evidenceItems.push({
      id: `ev_shift_${business.id}`,
      businessId: business.id || "",
      evidenceType: "repeatedJobPattern",
      audienceModes: ["staffing_agency", "workforce_organization"],
      fact: "Recurring shift-based or high-volume operational hiring pattern detected",
      normalizedValue: true,
      sourceType: "business_database",
      sourceTier: "tier_2_trusted",
      sourceUrl: null,
      sourceTitle: "Workforce Shift Pattern Analysis",
      sourcePublishedAt: null,
      sourceModifiedAt: null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.85,
      confidence: 0.80,
      freshnessMultiplier: 1.0,
      polarity: "positive",
      status: "confirmed"
    });
  }

  // Growth & expansion evidence
  if (hasExpansion) {
    const expDate = bAny.source_published_at || bAny.source_updated_at || bAny.last_expansion_update_date || null;
    const { multiplier: freshMult } = calculateEvidenceFreshnessMultiplier("expansionAnnouncement", expDate, referenceNowISO);

    evidenceItems.push({
      id: `ev_expansion_${business.id}`,
      businessId: business.id || "",
      evidenceType: "expansionAnnouncement",
      audienceModes: ["business_development", "recruiter", "sales_professional", "business_explorer"],
      fact: "Verified geographic expansion or multi-location growth announced",
      normalizedValue: true,
      sourceType: "news",
      sourceTier: "tier_2_trusted",
      sourceUrl: null,
      sourceTitle: "Business Growth & Expansion Announcement",
      sourcePublishedAt: expDate,
      sourceModifiedAt: bAny.source_updated_at || null,
      firstSeenAt: bAny.first_seen_at || referenceNowISO,
      lastSeenAt: referenceNowISO,
      lastCheckedAt: referenceNowISO,
      reliability: 0.88,
      confidence: 0.82,
      freshnessMultiplier: freshMult,
      polarity: "positive",
      status: "confirmed"
    });
  }

  // Calculate freshness metrics from actual source timestamps
  const sourceDatesISO = evidenceItems
    .map(e => e.sourcePublishedAt || e.sourceModifiedAt)
    .filter(Boolean) as string[];

  const newestSourceISO = sourceDatesISO.length > 0
    ? sourceDatesISO.reduce((a, b) => new Date(a) > new Date(b) ? a : b)
    : (bAny.source_updated_at || bAny.source_published_at || null);

  const freshness: EvidenceFreshness = {
    newestEvidenceISO: newestSourceISO || null,
    oldestActiveEvidenceISO: sourceDatesISO.length > 0
      ? sourceDatesISO.reduce((a, b) => new Date(a) < new Date(b) ? a : b)
      : null,
    lastCheckedISO: referenceNowISO,
    decayAppliedFactor: 1.0
  };

  const officialSourcesCount = evidenceItems.filter(e => e.sourceTier === "tier_1_official").length;
  const externalSourcesCount = evidenceItems.filter(e => e.sourceTier === "tier_2_trusted" || e.sourceTier === "tier_3_public").length;
  const failedSourcesCount = websiteUrl && !websiteExists ? 1 : 0;

  const sourceCoverage: SourceCoverage = {
    pagesCrawledCount: pagesCrawled.length,
    officialSourcesCount,
    externalSourcesCount,
    failedSourcesCount,
    reliabilityBreakdown: {
      tier_1_official: evidenceItems.filter(e => e.sourceTier === "tier_1_official").length,
      tier_2_trusted: evidenceItems.filter(e => e.sourceTier === "tier_2_trusted").length,
      tier_3_public: evidenceItems.filter(e => e.sourceTier === "tier_3_public").length,
      tier_4_weak: evidenceItems.filter(e => e.sourceTier === "tier_4_weak").length
    }
  };

  // Audience-specific last updated dates & unconfirmed requirements
  const parsePublicDateISO = (val?: string | null): string | null => {
    if (!val || val === "Unknown" || val === "null" || val === "undefined") return null;
    const ms = new Date(val).getTime();
    return isNaN(ms) ? null : new Date(ms).toISOString();
  };

  const rawJobDate = hasDirectHiring ? ((business as any).last_hiring_update_date || (business as any).last_hiring_update || newestSourceISO) : newestSourceISO;
  const rawExpansionDate = hasExpansion ? ((business as any).last_expansion_update_date || newestSourceISO) : newestSourceISO;
  const rawWebsiteDate = website.lastModifiedAt || newestSourceISO;
  const rawDefaultDate = newestSourceISO;

  const audienceLastUpdatedDates: Record<AudienceModeId, string | null> = {
    job_seeker: parsePublicDateISO(rawJobDate),
    recruiter: parsePublicDateISO(rawJobDate),
    staffing_agency: parsePublicDateISO(rawDefaultDate),
    career_coach: parsePublicDateISO(rawDefaultDate),
    workforce_organization: parsePublicDateISO(rawDefaultDate),
    college_career_center: parsePublicDateISO(rawDefaultDate),
    sales_professional: parsePublicDateISO(rawWebsiteDate),
    business_development: parsePublicDateISO(rawExpansionDate),
    agency_freelancer: parsePublicDateISO(rawWebsiteDate),
    business_explorer: parsePublicDateISO(rawDefaultDate)
  };

  const unconfirmedRequirements: Record<AudienceModeId, string[]> = {
    job_seeker: hasDirectHiring ? [] : ["Active official job vacancy listings", "Current hiring contact"],
    recruiter: employment.hardToFillVacanciesCount > 0 ? [] : ["Confirmed specialized engineering or executive vacancies", "External recruiter vendor relationship"],
    staffing_agency: employment.shiftWorkRolesCount > 0 ? [] : ["High-volume shift or temp-to-hire labor demand", "Vendor procurement portal registration"],
    career_coach: employment.entryLevelRolesCount > 0 ? [] : ["Verified paid apprenticeship or career-transition training program"],
    workforce_organization: employment.secondChanceHiring ? [] : ["Formal community workforce board partnership", "No-experience required open positions"],
    college_career_center: employment.internshipsCount > 0 ? [] : ["Active university co-op or summer internship program"],
    sales_professional: website.exists ? [] : ["Verified owner/decision-maker email contact", "Specific software modernization budget"],
    business_development: growth.recentExpansionAnnounced ? [] : ["Formal affiliate or vendor registration portal", "Active multi-state expansion press release"],
    agency_freelancer: websiteIssues.length > 0 ? [] : ["Verified active digital re-branding or web redesign RFP"],
    business_explorer: []
  };

  return {
    businessId: business.id || "",
    engineVersion: "audience_evidence_v2",
    identity,
    categories,
    website,
    services,
    products,
    locations,
    employment,
    growth,
    digitalPresence,
    customerActivity,
    accessibility,
    paymentAndInsurance,
    partnerships,
    reputation,
    contactability,
    freshness,
    sourceCoverage,
    evidenceItems,
    auditsCompletedAtISO: referenceNowISO,
    audienceLastUpdatedDates,
    unconfirmedRequirements
  };
}
